export type TActiveRevision = {
  id: number;
  activatedAt: string | null;
  note: string | null;
} | null;

export type TRevisionStatus = "draft" | "active" | "inactive" | "discarded";

export type TRevisionSummary = {
  id: number;
  featureKey: string;
  status: TRevisionStatus;
  note: string | null;
  parentRevisionId: number | null;
  deficiencyId: number | null;
  entryCount: number;
  createdById: number | null;
  createdByName: string | null;
  createdAt: string;
  activatedById: number | null;
  activatedByName: string | null;
  activatedAt: string | null;
};

export type TPromptLint = {
  missingRequired: string[];
  unknown: string[];
};

export type TRebasePlan = {
  needed: boolean;
  activeRevisionId: number | null;
  conflicting: string[];
  incoming: string[];
  keeping: string[];
};

export type TRevisionEntry = {
  promptKey: string;
  label: string;
  description: string;
  content: string;
  activeContent: string;
  differsFromActive: boolean;
  editedSinceBase: boolean;
  requiredPlaceholders: string[];
  optionalPlaceholders: string[];
  featureFlag: string | null;
  lint: TPromptLint;
};

// A registered prompt the revision doesn't store, so it resolves to the code's text.
export type TCodeDefaultPrompt = {
  promptKey: string;
  label: string;
  description: string;
  content: string;
  featureFlag: string | null;
};

export type TRevisionDetail = TRevisionSummary & {
  entries: TRevisionEntry[];
  keysResolvedFromDefault: string[];
  codeDefaultPrompts: TCodeDefaultPrompt[];
  unregisteredKeys: string[];
  rebase: TRebasePlan | null;
  pinnedRevisionId: number | null;
  passedTestCaseCount: number;
  requiredPassedTestCaseCount: number;
};

export const STATUS_STYLES: Record<TRevisionStatus, string> = {
  draft: "bg-blue-100 text-blue-800",
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-600",
  discarded: "bg-gray-100 text-gray-400"
};

export function formatRevisionLabel(revision: TActiveRevision): string {
  if (!revision) return "No revision — code defaults";

  const activatedAt = revision.activatedAt ? new Date(revision.activatedAt).toLocaleDateString() : null;
  return activatedAt ? `Revision ${revision.id} · active since ${activatedAt}` : `Revision ${revision.id} · active`;
}

export function formatTimestamp(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "—";
}

export function hasLintErrors(lint: TPromptLint): boolean {
  return lint.missingRequired.length > 0 || lint.unknown.length > 0;
}
